import { Injectable } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';

@Injectable({
  providedIn: 'root'
})
export class PkceService {
  // sessionStorage (not localStorage) so the verifier auto-evicts with the tab/WebView session;
  // app-namespaced key so it's distinguishable from StorageService-managed `ls.*` keys.
  private readonly CODE_VERIFIER_KEY = 'printix.pkce.code_verifier';
  private logger: Logger;

  constructor(private loggingService: LoggingService) {
    this.logger = this.loggingService.getLogger('[PkceService]');
    this.logger.entry('ctor');
  }

  /**
   * Generates a random code verifier for PKCE (RFC 7636)
   * @returns Base64 URL-encoded random string (43-128 characters)
   */
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32); // Create a 32 byte array
    crypto.getRandomValues(array); // Fill with cryptographically secure random values
    return this.base64UrlEncode(array); // Encode to base64 URL format
  }

  /**
   * Creates SHA-256 hash of the code verifier
   * @param verifier - The code verifier string
   * @returns Promise resolving to base64 URL-encoded hash
   */
  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(hash));
  }

  /**
   * Base64 URL encoding (without padding)
   * @param buffer - Byte array to encode
   * @returns Base64 URL-encoded string
   */
  private base64UrlEncode(buffer: Uint8Array): string {
    const base64 = btoa(String.fromCharCode(...buffer));
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generates PKCE parameters and stores the verifier
   * Call this before redirecting to authorization endpoint
   * @returns Promise resolving to code challenge for authorization URL
   */
  public async generatePkceChallenge(): Promise<string> {
    // Never log the verifier or challenge values themselves — entry only.
    this.logger.info('generatePkceChallenge()');
    const verifier = this.generateCodeVerifier();
    const challenge = await this.generateCodeChallenge(verifier);
    // Store verifier for later use in token exchange
    sessionStorage.setItem(this.CODE_VERIFIER_KEY, verifier);

    return challenge;
  }

  /**
   * Retrieves and removes the stored code verifier
   * Call this when exchanging authorization code for tokens
   * @returns The code verifier or null if not found
   */
  public getAndClearCodeVerifier(): string | null {
    const methodName = 'getAndClearCodeVerifier()';
    const verifier = sessionStorage.getItem(this.CODE_VERIFIER_KEY);
    this.logger.info(methodName + ' verifier present: ' + !!verifier);
    sessionStorage.removeItem(this.CODE_VERIFIER_KEY);
    return verifier;
  }

  /**
   * Removes any stored code verifier without returning it.
   * Use from sign-out / de-auth paths so an abandoned verifier doesn't outlive the session.
   */
  public clearCodeVerifier(): void {
    this.logger.info('clearCodeVerifier()');
    sessionStorage.removeItem(this.CODE_VERIFIER_KEY);
  }
}
